import { TOKEN_KEY } from "@/lib/auth/PowtoonAuthContext";
import { createLogger } from "@/lib/frontend-logger";
import { CORRELATION_HEADER, nextCorrelationId } from "@/lib/correlation";
import { trackEvent } from "@/services/analytics";
import { ANALYTICS_CATEGORIES, ANALYTICS_ACTIONS } from "@/config/analytics-config";

const logger = createLogger("scorm.download");

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "scenario"
  );
}

export async function downloadScormPackage(input: {
  scenarioId: string;
  scenarioName: string;
  publicLinkToken: string;
  version: "1.2" | "2004";
}): Promise<void> {
  const token = window.sessionStorage.getItem(TOKEN_KEY);
  if (!token) throw new Error("Not authenticated with Powtoon");

  const correlationId = nextCorrelationId();
  const startedAt = performance.now();
  logger.info("SCORM package requested", {
    scenarioId: input.scenarioId, version: input.version, correlationId,
  });
  trackEvent({
    category: ANALYTICS_CATEGORIES.SCORM,
    action: ANALYTICS_ACTIONS.STARTED,
    label: input.version,
    extraData: { scenario_id: input.scenarioId },
  });
  const res = await fetch(`${SUPABASE_URL}/functions/v1/scorm-package`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      apikey: SUPABASE_KEY,
      authorization: `Bearer ${SUPABASE_KEY}`,
      "x-powtoon-token": token,
      [CORRELATION_HEADER]: correlationId,
    },
    body: JSON.stringify({
      scenarioId: input.scenarioId,
      scenarioName: input.scenarioName,
      publicLinkToken: input.publicLinkToken,
      learnerBaseUrl: window.location.origin,
      version: input.version,
    }),
  });
  const durationMs = Math.round(performance.now() - startedAt);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    logger.error("SCORM package failed", {
      scenarioId: input.scenarioId, version: input.version, status: res.status, durationMs, correlationId,
    });
    trackEvent({
      category: ANALYTICS_CATEGORIES.SCORM,
      action: ANALYTICS_ACTIONS.ERROR,
      label: input.version,
      extraData: { scenario_id: input.scenarioId, status: res.status },
    });
    throw new Error(`scorm-package ${res.status}: ${text.slice(0, 200)}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${slugify(input.scenarioName)}-scorm-${input.version}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  logger.info("SCORM package downloaded", {
    scenarioId: input.scenarioId, version: input.version, sizeBytes: blob.size, durationMs, correlationId,
  });
  trackEvent({
    category: ANALYTICS_CATEGORIES.SCORM,
    action: ANALYTICS_ACTIONS.COMPLETED,
    label: input.version,
    value: blob.size,
    extraData: { scenario_id: input.scenarioId, duration_ms: durationMs },
  });
}